import React from 'react';
import { uniq } from 'lodash';
import { Error500 } from '@components/common/hero/error/Error500';
import { GenericHelpPageLayout } from '@layout/GenericHelpPageLayout/GenericHelpPageLayout';
import { getServerSidePropsHelp } from '@layout/get-page-props';
import { withGlobalProvider } from 'providers/globalProvider';
import { storeWrapper } from '@state/store';
import { GetStaticProps } from 'next';
import { PageProps } from '@model/pages';
import { ContentfulApi } from '@model/contentful/service';
import { CmsPages } from '@model/contentful';
import { Page } from '@model/common';
import { isPageBlacklisted } from '@model/config/brands';
import { ImagesResponse } from '@model/iceberg';
import { Revalidate } from '@model/config/revalidate';
import { ERROR_CODES } from '@model/common/error-codes';
import { FooterApi } from '@model/iceberg/service/content';

/* ***************** *
 *       Types       *
 * ***************** */
export interface GenericHelpPageProps extends PageProps {
  cmsConfig: any;
  path: string;
  hero: ImagesResponse;
}

/* ********************* *
 *    GenericHelpPage    *
 * ********************* */
const GenericHelpPage = (props: GenericHelpPageProps) => {
  if (!props.cmsConfig) return <Error500 code={ERROR_CODES.NO_CMS_CONFIG} />;

  return <GenericHelpPageLayout {...props} />;
};

export const getStaticProps: GetStaticProps = storeWrapper.getStaticProps((store) => async ({ params }: any) => {
  const pageId: string = params.pageId;
  const path: string = Page.HELP.concat('/').concat(params.pageId);
  const props = await getServerSidePropsHelp({ store, pageId, path });

  return {
    ...props,
    revalidate: Revalidate.HALF_HOUR
  };
});

export async function getStaticPaths() {
  const cmsConfig = await new ContentfulApi().getPageConfig({ includeLevel: 3, pageId: CmsPages.HELP_PAGE });
  const footer = await new FooterApi().getMainFooter();
  const footerLinks = footer.data.links.map(({ url }) => url).filter((url) => url.includes(Page.HELP));

  const infoPages = cmsConfig.items[0].fields.components[0].fields.infoTiles.filter((infoTile: any) => {
    const pageId = infoTile.fields?.pageId;
    return !!pageId && !isPageBlacklisted(pageId);
  });

  const paths: any = uniq(infoPages)
    .map((page: any) => Page.HELP.concat(`/${page.fields?.pageId}`))
    .concat(footerLinks);
  return { paths, fallback: false };
}

export default withGlobalProvider(GenericHelpPage);
